import React, { useState, useEffect } from 'react';
import { PortvilleBill } from '../../../../types/portvilleBilling';

interface BillsTableProps {
  bills: PortvilleBill[];
  loading: boolean;
  onBillDetail: (bill: PortvilleBill) => void;
  onUpdateBill: (billId: string, updates: Partial<PortvilleBill>) => void;
  onDeleteBill: (billId: string) => void;
  onPrintPDF?: (bill: PortvilleBill) => void;
  onBulkStatusUpdate?: (billIds: string[], status: PortvilleBill['status']) => void;
  onPenaltyInput?: (bill: PortvilleBill) => void;
}

const BillsTable: React.FC<BillsTableProps> = ({
  bills,
  loading,
  onBillDetail,
  onUpdateBill,
  onDeleteBill,
  onPrintPDF,
  onBulkStatusUpdate,
  onPenaltyInput
}) => {
  // 선택된 청구서 상태 관리
  const [selectedBills, setSelectedBills] = useState<Set<string>>(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // 청구서 목록이 변경될 때 선택 상태 초기화
  useEffect(() => {
    setSelectedBills(new Set());
    setIsAllSelected(false);
    setShowBulkActions(false);
  }, [bills]);

  // 전체 선택/해제
  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedBills(new Set());
      setIsAllSelected(false);
      setShowBulkActions(false);
    } else {
      const allBillIds = new Set(bills.map(bill => bill.id));
      setSelectedBills(allBillIds);
      setIsAllSelected(true);
      setShowBulkActions(true);
    }
  };

  // 개별 청구서 선택/해제
  const handleSelectBill = (billId: string) => {
    const newSelected = new Set(selectedBills);
    if (newSelected.has(billId)) {
      newSelected.delete(billId);
    } else {
      newSelected.add(billId);
    }
    setSelectedBills(newSelected);
    setIsAllSelected(newSelected.size === bills.length && bills.length > 0);
    setShowBulkActions(newSelected.size > 0);
  };

  // 일괄 상태 변경
  const handleBulkStatusChange = (status: PortvilleBill['status']) => {
    if (selectedBills.size === 0) return;
    
    if (window.confirm(`선택된 ${selectedBills.size}개의 청구서를 "${getStatusText(status)}" 상태로 변경하시겠습니까?`)) {
      onBulkStatusUpdate?.(Array.from(selectedBills), status);
      setSelectedBills(new Set());
      setIsAllSelected(false);
      setShowBulkActions(false);
    }
  };
  // 상태 스타일 반환
  const getStatusStyle = (status: PortvilleBill['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // 상태 텍스트 반환
  const getStatusText = (status: PortvilleBill['status']) => {
    switch (status) {
      case 'pending':
        return '대기중';
      case 'paid':
        return '납부완료';
      case 'overdue':
        return '연체';
      case 'cancelled':
        return '취소';
      default:
        return '알 수 없음';
    }
  };

  // 상태 변경 핸들러
  const handleStatusChange = (bill: PortvilleBill, newStatus: PortvilleBill['status']) => {
    const updates: Partial<PortvilleBill> = { 
      status: newStatus,
      updatedAt: new Date()
    };

    // 납부완료로 변경시 납부일 추가
    if (newStatus === 'paid') {
      updates.paidDate = new Date();
    }

    onUpdateBill(bill.id, updates);
  };

  // 로딩 상태
  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          {Array.from({length: 5}).map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  // 빈 상태
  if (bills.length === 0) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-500">
          <div className="text-4xl mb-4">📋</div>
          <h3 className="text-lg font-medium mb-2">청구서가 없습니다</h3>
          <p className="text-sm">선택한 조건에 맞는 청구서가 없습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* 일괄 작업 UI */}
      {showBulkActions && (
        <div className="bg-blue-50 border-b border-blue-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900">
                {selectedBills.size}개 선택됨
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={handleSelectAll}
                  className="bg-blue-100 text-blue-800 px-3 py-1 rounded-md text-sm font-medium hover:bg-blue-200"
                >
                  {isAllSelected ? '전체 선택취소' : '전체 선택'}
                </button>
                <button
                  onClick={() => {
                    setSelectedBills(new Set());
                    setIsAllSelected(false);
                    setShowBulkActions(false);
                  }}
                  className="bg-gray-100 text-gray-800 px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-200"
                >
                  선택 취소
                </button>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleBulkStatusChange('pending')}
                  className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-md text-sm font-medium hover:bg-yellow-200"
                >
                  대기중으로 변경
                </button>
                <button
                  onClick={() => handleBulkStatusChange('paid')}
                  className="bg-green-100 text-green-800 px-3 py-1 rounded-md text-sm font-medium hover:bg-green-200"
                >
                  납부완료로 변경
                </button>
                <button
                  onClick={() => handleBulkStatusChange('overdue')}
                  className="bg-red-100 text-red-800 px-3 py-1 rounded-md text-sm font-medium hover:bg-red-200"
                >
                  연체로 변경
                </button>
                <button
                  onClick={() => handleBulkStatusChange('cancelled')}
                  className="bg-gray-100 text-gray-800 px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-200"
                >
                  취소로 변경
                </button>
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedBills(new Set());
                setIsAllSelected(false);
                setShowBulkActions(false);
              }}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              일괄변경 종료
            </button>
          </div>
        </div>
      )}

      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={handleSelectAll}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              호실
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              입주자
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              청구 기간
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              총 금액
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              상태
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              납부 기한
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              작업
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {bills.map((bill) => (
            <tr key={bill.id} className={`hover:bg-gray-50 ${selectedBills.has(bill.id) ? 'bg-blue-50' : ''}`}>
              <td className="px-6 py-4 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={selectedBills.has(bill.id)}
                  onChange={() => handleSelectBill(bill.id)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {bill.roomNumber}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {bill.roomType === '시설물' && bill.facilityInfo ? (
                  <>
                    <div className="text-sm text-gray-900">{bill.facilityInfo.facilityName}</div>
                    <div className="text-sm text-gray-500">{bill.facilityInfo.responsiblePerson.name}</div>
                  </>
                ) : bill.residentInfo ? (
                  <>
                    <div className="text-sm text-gray-900">{bill.residentInfo.name}</div>
                    <div className="text-sm text-gray-500">{bill.residentInfo.organization}</div>
                  </>
                ) : (
                  <div className="text-sm text-gray-500">정보 없음</div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {bill.billingPeriod.year}년 {bill.billingPeriod.month}월
                </div>
                <div className="text-sm text-gray-500">
                  {new Date(bill.billingPeriod.startDate).toLocaleDateString()} ~ 
                  {new Date(bill.billingPeriod.endDate).toLocaleDateString()}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {bill.totalAmount.toLocaleString()}원
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <select
                  value={bill.status}
                  onChange={(e) => handleStatusChange(bill, e.target.value as PortvilleBill['status'])}
                  className={`text-xs font-semibold px-2 py-1 rounded-full border-0 ${getStatusStyle(bill.status)}`}
                >
                  <option value="pending">대기중</option>
                  <option value="paid">납부완료</option>
                  <option value="overdue">연체</option>
                  <option value="cancelled">취소</option>
                </select>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {new Date(bill.dueDate).toLocaleDateString()}
                </div>
                {bill.paidDate && (
                  <div className="text-sm text-green-600">
                    납부: {new Date(bill.paidDate).toLocaleDateString()}
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex space-x-2">
                  <button
                    onClick={() => onBillDetail(bill)}
                    className="text-blue-600 hover:text-blue-900 text-sm"
                  >
                    상세
                  </button>
                  {onPrintPDF && (
                    <button
                      onClick={() => onPrintPDF(bill)}
                      className="text-green-600 hover:text-green-900 text-sm"
                    >
                      PDF
                    </button>
                  )}
                  {bill.status === 'overdue' && onPenaltyInput && (
                    <button
                      onClick={() => onPenaltyInput(bill)}
                      className="text-orange-600 hover:text-orange-900 text-sm"
                    >
                      가산금
                    </button>
                  )}
                  <button
                    onClick={() => onDeleteBill(bill.id)}
                    className="text-red-600 hover:text-red-900 text-sm"
                  >
                    삭제
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 페이지네이션 (필요시 구현) */}
      {bills.length > 0 && (
        <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              총 <span className="font-medium">{bills.length}</span>개의 청구서
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillsTable;
